import { cn } from "@/lib/utils/cn";

interface KpiCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  tint?: "brand" | "success" | "info" | "warning" | "pink" | "orange";
  description?: string;
  trend?: {
    value: string;
    isUp: boolean;
  };
}

const tints = {
  brand: "bg-brand-500/15 text-brand-600",
  success: "bg-success-soft text-success",
  info: "bg-info-soft text-info",
  warning: "bg-warning-soft text-warning",
  pink: "bg-pink-soft text-pink",
  orange: "bg-orange-soft text-orange",
};

export function KpiCard({ label, value, icon, tint = "brand", description, trend }: KpiCardProps) {
  return (
    <div className="group relative overflow-hidden rounded-2xl border border-white/40 bg-white/70 p-5 shadow-card backdrop-blur-sm transition-all hover:scale-[1.02] hover:shadow-argon-hover">
      {/* Decorative gradient blob inside card */}
      <div 
        aria-hidden 
        className={cn(
          "absolute -right-4 -top-4 h-20 w-20 rounded-full opacity-5 blur-2xl transition-opacity group-hover:opacity-10",
          tint === "brand" ? "bg-brand-500" :
          tint === "success" ? "bg-success" :
          tint === "warning" ? "bg-warning" : "bg-info"
        )} 
      />

      <div className="relative flex items-center justify-between">
        <div className="space-y-1">
          <p className="text-[11px] font-bold uppercase tracking-wider text-ink-soft">{label}</p>
          <p className="text-2xl font-black tracking-tight text-ink">{value}</p>
        </div>
        <div className={cn("flex h-12 w-12 items-center justify-center rounded-xl shadow-inner-light", tints[tint])}>
          {icon}
        </div>
      </div>

      {(description || trend) && (
        <div className="mt-4 flex items-center gap-2 border-t border-border-soft pt-3">
          {trend && (
            <span className={cn("text-xs font-bold", trend.isUp ? "text-success" : "text-danger")}>
              {trend.isUp ? "↑" : "↓"} {trend.value}
            </span>
          )}
          {description && <p className="text-[11px] font-medium text-ink-muted">{description}</p>}
        </div>
      )}
    </div>
  );
}
